import { MutationFunctionOptions } from '@apollo/client/react/types/types'
import { CriteriaOperationType } from '@models'
import { storage } from '@services/storageService'
import { hasUserRoles, PERMISSION_MAP } from '@services/userRoleService'
import { apolloClient, stores, strings } from '@stores'
import { ExecutionResult } from 'graphql/execution'
import cloneDeep from 'lodash/cloneDeep'
import includes from 'lodash/includes'
import meanBy from 'lodash/meanBy'
import map from 'lodash/map'
import isEqual from 'lodash/isEqual'
import filter from 'lodash/filter'
import { action, computed, observable } from 'mobx'
import { GET_BUYING_SESSION_PRODUCT_DETAILS } from '../graphql/findBuyingSessionProductDetails'
import { GET_BUYING_SESSION_PRODUCT_COMMENTS } from '../graphql/getBuyingSessionProductComments'
import { GET_BUYING_SESSION_PRODUCT_RATING } from '../graphql/getBuyingSessionProductRating'
import {
  IDeleteProductComment,
  IExcludeClientFromBuyingSessionProduct,
  IExcludeClusterFromBSP,
  IProductComment,
  IProductRating,
  IRemoveClientFromBuyingSessionProductExclusion,
  IUpdateProductComment
} from '../graphql/hooks'
import { computedFn } from 'mobx-utils'
import difference from 'lodash/difference'
import { getEnumValue } from '@utils/get-enum-value'
import { config } from '@config'
import axios from 'axios'
import {
  addOrRemoveClientExclusion,
  deleteForecastFromOrderMgmtSvc
} from '../../../../services/productDetailService'
import { productForecastUIStore } from '@modules/wholesale/buyingSessionGroupProductWrapper/stores/productForecastUIStore'
import { syncToBsgElasticSearch } from '@services/commonServices'

export class ProductDetailStore {
  @observable buyingSessionGroupId: string
  @observable buyingSessionProductId: string
  @observable commentText = ''
  @observable commentBeingSaved = new Map()
  @observable selectedCommentId = null
  @observable editedCommentText = ''
  @observable isSaveCommentInProgress = false
  @observable selectedRows = []
  @observable isClientExclusionInProcess = false
  @observable clientsRemovedFromExclusion = new Map()
  @observable isCoreInProgress = false

  // Cluster Exclusion
  @observable isClusterExclusionInProcess = false

  @observable addChannelPopupAnchorEl = false

  @observable isClientExclusion = false
  @observable isZoneExclusion = false

  mutations: {
    updateBuyingSessionProductRating?: (
      options?: MutationFunctionOptions<any, IProductRating>
    ) => Promise<void | ExecutionResult>
    addBuyingSessionProductComment?: (
      options?: MutationFunctionOptions<any, IProductComment>
    ) => Promise<void | ExecutionResult>
    updateBuyingSessionProductComment?: (
      options?: MutationFunctionOptions<any, IUpdateProductComment>
    ) => Promise<void | ExecutionResult>
    deleteBuyingSessionProductComment?: (
      options?: MutationFunctionOptions<any, IDeleteProductComment>
    ) => Promise<void | ExecutionResult>
    excludeClientsFromBuyingSessionProduct?: (
      options?: MutationFunctionOptions<any, IExcludeClientFromBuyingSessionProduct>
    ) => Promise<void | ExecutionResult>
    removeClientFromBuyingSessionProductExclusion?: (
      options?: MutationFunctionOptions<
        any,
        IRemoveClientFromBuyingSessionProductExclusion
      >
    ) => Promise<void | ExecutionResult>
    addUpdateExcludedClustersForBSP?: (
      options?: MutationFunctionOptions<any, IExcludeClusterFromBSP>
    ) => Promise<void | ExecutionResult>
  } = {}

  initStore = (buyingSessionGroupId, buyingSessionProductId) => {
    this.buyingSessionGroupId = buyingSessionGroupId
    this.buyingSessionProductId = buyingSessionProductId
  }

  clearStore = () => {
    this.buyingSessionGroupId = undefined
    this.buyingSessionProductId = undefined
    this.selectedCommentId = null
    this.commentText = ''
    this.editedCommentText = ''
  }

  @computed get buyingSessionProductWhere() {
    const {
      nav: {
        queryParams: { bspId }
      }
    } = stores
    if (bspId) {
      return { Id: { operation: CriteriaOperationType.EQ, value: bspId } }
    } else {
      return null
    }
  }

  /**
   * updateBuyingSessionProductRating
   * @param buyingSessionProductId - Product id
   * @param ratingType - rating type
   *@param ratingValue - rating value
   */
  @action updateBuyingSessionProductRating = async ({
    buyingSessionProduct,
    ratingType,
    ratingValue,
    previousValue
  }) => {
    const {
      showroomProductDetailStore: { updateProcessedProductRating }
    } = stores
    try {
      const value = ratingValue === previousValue && ratingValue === 1 ? 0 : ratingValue
      const currentUser: { id: string } = storage.getItem('user')
      const refetchQueries = [
        {
          query: GET_BUYING_SESSION_PRODUCT_RATING,
          variables: {
            userId: currentUser.id,
            buyingSessionProductId: buyingSessionProduct
          }
        }
      ]

      await this.mutations.updateBuyingSessionProductRating({
        variables: {
          buyingSessionProduct,
          ratingType,
          ratingValue: value
        },
        refetchQueries,
        update: async proxy => {
          const { data } = await apolloClient.query({
            query: GET_BUYING_SESSION_PRODUCT_RATING,
            variables: {
              buyingSessionProductId: buyingSessionProduct
            },
            fetchPolicy: 'network-only'
          })
          if (data) {
            const ratings = data?.findVIPRating?.Items ?? null
            // note: average is considered for non-zero ratings only
            const nonZeroRatings = ratings.filter(rating => rating.Value !== 0)
            const averageRating = meanBy(nonZeroRatings, 'Value') || 0

            // update prod detail rating first -> positive outlook
            updateProcessedProductRating(ratingType, ratingValue)

            const buyingSessionProductData = apolloClient.readQuery({
              query: GET_BUYING_SESSION_PRODUCT_DETAILS,
              variables: {
                where: this.buyingSessionProductWhere
              }
            })
            const productData = cloneDeep(buyingSessionProductData)
            productData.findVIPBuyingSessionProduct.Items[0].averageRating = averageRating
            proxy.writeQuery({
              query: GET_BUYING_SESSION_PRODUCT_DETAILS,
              variables: {
                where: this.buyingSessionProductWhere
              },
              data: productData
            })
          }
        }
      })
      syncToBsgElasticSearch([buyingSessionProduct])
    } catch (err) {
      // revert prod detail rating in case of failure
      updateProcessedProductRating(ratingType, previousValue)
    }
  }

  @action
  addBuyingSessionProductComment = async () => {
    const {
      nav: {
        queryParams: { bspId: _bspId }
      }
    } = stores
    const bspId = this.buyingSessionProductId || _bspId
    if (bspId) {
      try {
        this.isSaveCommentInProgress = true
        const refetchQuery = [
          {
            query: GET_BUYING_SESSION_PRODUCT_COMMENTS,
            variables: {
              buyingSessionProductId: bspId
            }
          }
        ]

        await this.mutations.addBuyingSessionProductComment({
          variables: {
            buyingSessionProduct: bspId,
            comment: this.commentText
          },
          refetchQueries: refetchQuery,
          update: async proxy => {
            const buyingSessionProductData = apolloClient.readQuery({
              query: GET_BUYING_SESSION_PRODUCT_DETAILS,
              variables: {
                where: this.buyingSessionProductWhere
              }
            })
            const productData = cloneDeep(buyingSessionProductData)
            productData.findVIPBuyingSessionProduct.Items[0].hasComments = true
            proxy.writeQuery({
              query: GET_BUYING_SESSION_PRODUCT_DETAILS,
              variables: {
                where: this.buyingSessionProductWhere
              },
              data: productData
            })
          }
        })
        syncToBsgElasticSearch([bspId])
      } finally {
        this.commentText = ''
        this.isSaveCommentInProgress = false
      }
    }
  }

  @action
  updateBuyingSessionProductComment = async () => {
    const {
      nav: {
        queryParams: { bspId: _bspId }
      }
    } = stores
    const bspId = this.buyingSessionProductId || _bspId
    if (bspId) {
      try {
        const refetchQuery = [
          {
            query: GET_BUYING_SESSION_PRODUCT_COMMENTS,
            variables: {
              buyingSessionProductId: bspId
            }
          }
        ]
        await this.mutations.updateBuyingSessionProductComment({
          variables: {
            commentId: this.selectedCommentId,
            commentText: this.editedCommentText
          },
          refetchQueries: refetchQuery
        })
        syncToBsgElasticSearch([bspId])
      } finally {
        this.selectedCommentId = null
        this.editedCommentText = ''
      }
    }
  }

  @action
  deleteBuyingSessionProductComment = async commentId => {
    const {
      nav: {
        queryParams: { bspId: _bspId }
      }
    } = stores
    const bspId = this.buyingSessionProductId || _bspId
    if (commentId) {
      try {
        await this.mutations.deleteBuyingSessionProductComment({
          variables: {
            commentId
          },
          update: async proxy => {
            const { data } = await apolloClient.query({
              query: GET_BUYING_SESSION_PRODUCT_COMMENTS,
              variables: {
                buyingSessionProductId: bspId
              },
              fetchPolicy: 'network-only'
            })
            if (data) {
              const comment =
                data?.getVIPBuyingSessionProduct?.DocumentsAndComments?.Comments ?? null
              const hasComments = !!comment
              const buyingSessionProductData = apolloClient.readQuery({
                query: GET_BUYING_SESSION_PRODUCT_DETAILS,
                variables: {
                  where: this.buyingSessionProductWhere
                }
              })
              const productData = cloneDeep(buyingSessionProductData)
              productData.findVIPBuyingSessionProduct.Items[0].hasComments = hasComments
              proxy.writeQuery({
                query: GET_BUYING_SESSION_PRODUCT_DETAILS,
                variables: {
                  where: this.buyingSessionProductWhere
                },
                data: productData
              })
            }
          }
        })
        syncToBsgElasticSearch([bspId])
      } catch (e) {
        console.error('Error while deleting comment for buying session product', e)
      }
    }
  }

  @action
  handleCommentTextChange = commentId => event => {
    if (commentId) {
      this.editedCommentText = event.target.value
    } else {
      this.commentText = event.target.value
    }
  }

  @action handleEditComment = (commentId, commentText) => {
    this.selectedCommentId = commentId
    this.editedCommentText = commentText
  }

  @action setClientExclusion = (value: boolean) => {
    this.isClientExclusion = value
  }

  @action setZoneExclusion = (value: boolean) => {
    this.isZoneExclusion = value
  }

  setSelectedData = selectedRows => {
    this.selectedRows = selectedRows
  }

  filterExcludedClients = (clients, excludedClientsForBSP, excludedClientsForBSG) => {
    const excludedClients = map(
      [...excludedClientsForBSP, ...excludedClientsForBSG],
      'Id'
    )
    const filteredClients = clients.filter(
      client => !includes(excludedClients, client.Id) && client.GIV_VIPClient_Active_bool
    )
    return filteredClients
  }

  @action
  excludeClientsFromBuyingSessionProduct = async (previousSelectedRows: any) => {
    const {
      clientSearch: { handlePopOverClose }
    } = stores

    handlePopOverClose()

    if (this.buyingSessionProductId) {
      this.isClientExclusionInProcess = true
      try {
        const clients = map([...this.selectedRows], 'id') || []
        const previousClients = map([...previousSelectedRows], 'id') || []
        const addClients = []
        const removeClients = []

        clients.forEach(id => {
          if (!previousClients.includes(id)) addClients.push(id)
        })
        previousClients.forEach(id => {
          if (!clients.includes(id)) removeClients.push(id)
        })
        if (!addClients.length && !removeClients) return

        const { data } = await addOrRemoveClientExclusion(this.buyingSessionProductId, {
          addClients,
          removeClients
        })

        if (data?.status === 'Success') {
          await apolloClient.query({
            query: GET_BUYING_SESSION_PRODUCT_DETAILS,
            variables: {
              where: {
                Id: {
                  operation: CriteriaOperationType.EQ,
                  value: this.buyingSessionProductId
                }
              }
            },
            fetchPolicy: 'network-only'
          })
        }

        if (this.isZoneExclusion)
          await deleteForecastFromOrderMgmtSvc(
            this.buyingSessionGroupId,
            this.buyingSessionProductId,
            clients
          )
        syncToBsgElasticSearch([this.buyingSessionProductId])
      } finally {
        // this.selectedRows = []
        this.isClientExclusionInProcess = false
      }
    }
  }

  @action
  removeClientFromBuyingSessionProductExclusion = (clientId: any) => async e => {
    if (this.buyingSessionProductId && clientId) {
      this.clientsRemovedFromExclusion.set(clientId, true)
      try {
        const refetchQuery = [
          {
            query: GET_BUYING_SESSION_PRODUCT_DETAILS,
            variables: {
              where: this.buyingSessionProductWhere
            }
          }
        ]
        await this.mutations.removeClientFromBuyingSessionProductExclusion({
          variables: {
            buyingSessionProduct: this.buyingSessionProductId,
            client: clientId
          },
          awaitRefetchQueries: true,
          refetchQueries: refetchQuery
        })
      } finally {
        this.clientsRemovedFromExclusion.delete(clientId)
      }
    }
  }

  isDeleteInProgress = Id => {
    const client = this.clientsRemovedFromExclusion.get(Id)
    return client ? true : false
  }

  // Cluster Exclusion
  @action
  addUpdateExcludedClustersForBSP = async () => {
    const {
      clusterExclusionMultiSelect: {
        handleClusterPopOverClose,
        selectedClusters,
        newSelectedClusters
      }
    } = stores
    const isEqualClusters = isEqual(selectedClusters, newSelectedClusters)
    if (isEqualClusters) handleClusterPopOverClose()
    else if (this.buyingSessionProductId && !isEqualClusters) {
      this.isClusterExclusionInProcess = true
      const clusters = map(newSelectedClusters, 'Id')
      try {
        const refetchQuery = [
          {
            query: GET_BUYING_SESSION_PRODUCT_DETAILS,
            variables: {
              where: this.buyingSessionProductWhere
            }
          }
        ]
        await this.mutations.addUpdateExcludedClustersForBSP({
          variables: {
            buyingSessionProduct: this.buyingSessionProductId,
            clusters
          },
          refetchQueries: refetchQuery
        })
        syncToBsgElasticSearch([this.buyingSessionProductId])
      } finally {
        handleClusterPopOverClose()
        this.isClusterExclusionInProcess = false
      }
    }
  }

  @action
  addChannelAndSync = async channel => {
    const {
      wsBuyingSessionProduct: { addChannelToBuyingSessionProduct }
    } = stores
    await addChannelToBuyingSessionProduct(this.buyingSessionProductId, channel)
    this.closeAddChannelPopup()
    syncToBsgElasticSearch([this.buyingSessionProductId])
  }

  @action openAddChannelPopup = event => {
    this.addChannelPopupAnchorEl = event.currentTarget
  }

  @action closeAddChannelPopup = () => {
    this.addChannelPopupAnchorEl = null
  }

  getChannelsToAdd = computedFn((bsgChannels, bspChannels) => {
    const bspChannelValues = bspChannels?.map(channel => channel.id)
    const allowedBSGChannels = filter(bsgChannels, channel =>
      hasUserRoles(PERMISSION_MAP.find(ele => ele.id === channel)?.allowedRoles)
    )
    const channels = difference(allowedBSGChannels, bspChannelValues)
    return channels?.map(channel => {
      return { name: getEnumValue(channel), value: channel }
    })
  })

  @action handleProductStatusChange = async e => {
    this.isCoreInProgress = true
    const buyingSessionProductId = this.buyingSessionProductId
    const isCore: boolean = e.target.value
    const url = `${config.buyingBoardServiceRestEndpoint}buyingSessionGroup/${this.buyingSessionGroupId}/product/markProductAsCore`
    const body = {
      buyingSessionProductId,
      isCore
    }

    const sessionURL: string = storage.getItem('access_token')
    try {
      const { data } = await axios.post(url, body, {
        headers: {
          Authorization: `Bearer ${sessionURL}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (data?.Status === strings.centricCallSuccess) {
        const {
          nav: {
            queryParams: { productLayout }
          }
        } = stores
        const { setForecastData } = productForecastUIStore
        await apolloClient.query({
          query: GET_BUYING_SESSION_PRODUCT_DETAILS,
          variables: {
            where: {
              // get bspId irrespective of the query params GBB-7484
              Id: { operation: CriteriaOperationType.EQ, value: buyingSessionProductId }
            }
          },
          fetchPolicy: 'network-only'
        })
        'list' === productLayout && e.target.value && (await setForecastData(null, true))
      }
    } finally {
      this.isCoreInProgress = false
    }
  }
}

export const productDetail = new ProductDetailStore()
